import { useMemo } from "react";
import { useAuthStore, type UserPermissionGroup } from "@/store/authStore";
import { useLoadedRoleCatalog } from "@/hooks/useRoleCatalog";
import type { CredentialId } from "@/lib/api/credentials";
import {
  getJwtBoolClaim,
  platformAssignment as assignmentOf,
} from "@/lib/auth/jwt";
import { Role } from "@/lib/constants/roles";

/** What a permission is asked about, when it is asked about one object. */
export interface PermissionResource {
  /** The account the object belongs to. */
  credentialId?: CredentialId;
  /**
   * The object's organization, then each organization above it, nearest first
   * (a company, its MSP, its distributor). Organization scope is applied only
   * when this is given; the server applies it regardless.
   */
  organizationPath?: readonly string[];
}

/**
 * Non-admin roles that rely on JWT role + merged permission group matrix from GET /auth/me/.
 * Platform / distributor / MSP / company admins are "break-glass": `can()` short-circuits true for
 * them, except that restricted platform staff asked about an organization path are confined to
 * their assignment — server-side enforcement remains authoritative.
 *
 * **Backend source of truth (keep in sync when adding modules/actions):**
 * - `everythingcloud-backend/core/permissions/groups.py` — `GroupBasedPermission`
 * - `everythingcloud-backend/core/permissions/finops.py` — `FinOps*` / `CredentialsCompanyAdminOrGroupPermission`
 * - `everythingcloud-backend/api/v1/groups/serializers.py` — `GroupPermissionUpdateSerializer.valid_*`
 *
 * FinOps and credentials APIs enforce the matrix in DRF; this hook + `CredentialsRouteGuard` are UX-only.
 * Contract: regenerate `openapi-schema.yaml` and run `npm run generate:api` when API shapes change.
 */
const GROUP_MATRIX_ROLES: readonly string[] = [
  Role.TEAM_MEMBER,
  Role.READ_ONLY,
  Role.ACCOUNTING,
];

/**
 * What a role holds by itself, before any group. The server serves this on the
 * role catalog (`baseline`, from `core.permissions.groups.ROLE_BASELINE`) and
 * that copy wins once loaded; this one answers only until then (first paint,
 * and a session whose catalog read failed).
 */
const FALLBACK_BASELINE: Record<string, Record<string, readonly string[]>> = {
  [Role.ACCOUNTING]: {
    cost_analysis: ["view", "export"],
    reports: ["view", "create", "edit", "delete", "export", "schedule"],
    inventory: ["view"],
    recommendations: ["view"],
    scheduling: ["view"],
  },
  [Role.READ_ONLY]: {
    cost_analysis: ["view"],
    reports: ["view"],
    inventory: ["view"],
    recommendations: ["view"],
    scheduling: ["view"],
  },
};

/**
 * Read-only is a ceiling: no group grant makes it write (the server refuses
 * at both its route and object checks). Viewing and exporting change nothing.
 */
const READ_ONLY_ACTIONS: readonly string[] = ["view", "export"];

export function usePermissions() {
  const { user, accessToken } = useAuthStore();
  const servedCatalog = useLoadedRoleCatalog();

  const userGroups = useMemo<UserPermissionGroup[]>(
    () => user?.permissionGroups ?? [],
    [user?.permissionGroups],
  );

  const baseline = useMemo<Record<string, readonly string[]>>(() => {
    const role = user?.role ?? "";
    const served = servedCatalog?.find((r) => r.code === role)?.baseline;
    return served ?? FALLBACK_BASELINE[role] ?? {};
  }, [servedCatalog, user?.role]);

  /**
   * The organizations restricted platform staff are assigned to, from the
   * token's `allowed_organization_ids`. `undefined` for an unrestricted
   * platform admin, and for a token that does not carry the claim: unknown
   * is not "nothing", so those keep the role's answer.
   */
  const platformAssignment = useMemo<readonly string[] | undefined>(
    () => assignmentOf(user?.role, accessToken),
    [user?.role, accessToken],
  );

  const can = useMemo(
    () =>
      (module: string, action: string, resource?: PermissionResource): boolean => {
        if (
          user?.role === Role.PLATFORM_ADMIN ||
          user?.role === Role.PLATFORM_MANAGER
        ) {
          // Restricted staff reach their assigned organizations and everything
          // beneath them, so any organization on the path being assigned grants.
          const path = resource?.organizationPath;
          if (!path || !platformAssignment) return true;
          return path.some((id) => platformAssignment.includes(id));
        }

        if (
          user?.role === Role.DISTRIBUTOR_ADMIN ||
          user?.role === Role.MSP_ADMIN ||
          user?.role === Role.COMPANY_OWNER ||
          user?.role === Role.COMPANY_ADMIN
        ) {
          return true;
        }

        if (!user?.role || !GROUP_MATRIX_ROLES.includes(user.role)) {
          return false;
        }

        if (
          user.role === Role.READ_ONLY &&
          !READ_ONLY_ACTIONS.includes(action)
        ) {
          return false;
        }

        const credentialId = resource?.credentialId;
        const path = resource?.organizationPath;

        // Read-only and accounting are pinned to their own company: a fence,
        // not a grant, whatever a group says.
        if (path) {
          const inOwnCompany =
            user.companyId != null && path[0] === user.companyId;
          if (user.role === Role.READ_ONLY && !inOwnCompany) return false;
          if (
            user.role === Role.ACCOUNTING &&
            user.companyId != null &&
            !inOwnCompany
          ) {
            return false;
          }
        }

        // The role's own baseline, narrowed the way the server narrows it:
        // groups granting the module's view that all name accounts limit the
        // role to those accounts; none, or one naming none, leaves it whole.
        if (baseline[module]?.includes(action)) {
          if (credentialId == null) return true;
          const viewGroups = userGroups.filter(
            (g) => g.permissions[module]?.view === true,
          );
          const named = (g: UserPermissionGroup) =>
            g.allowed_credential_ids ?? [];
          if (!viewGroups.length || viewGroups.some((g) => !named(g).length)) {
            return true;
          }
          if (viewGroups.some((g) => named(g).includes(credentialId))) {
            return true;
          }
        }

        // Any group may grant it, as on the server, which walks every group
        // before refusing. Asked about an object, a group grants when it names
        // the object's account; when it reaches every company under the
        // member's own MSP and the object is under it; when it names the
        // object's organization itself (exactly, not what sits beneath it);
        // or, naming nothing at all, when the object sits under the group's
        // anchor (a system group has none and reaches everything).
        for (const group of userGroups) {
          if (!group.permissions[module]?.[action]) continue;
          if (credentialId == null && !path) return true;
          const creds = group.allowed_credential_ids ?? [];
          if (credentialId != null && creds.includes(credentialId)) return true;
          if (!path) {
            // No organization to ask about: a group naming no accounts grants.
            if (creds.length === 0) return true;
            continue;
          }
          const orgs = group.allowed_organization_ids ?? [];
          if (
            group.can_view_all_companies &&
            user.mspId != null &&
            path.includes(user.mspId)
          ) {
            return true;
          }
          if (orgs.includes(path[0])) return true;
          if (
            orgs.length === 0 &&
            creds.length === 0 &&
            (group.organization_id == null ||
              path.includes(group.organization_id))
          ) {
            return true;
          }
        }

        return false;
      },
    [
      user?.role,
      user?.companyId,
      user?.mspId,
      userGroups,
      baseline,
      platformAssignment,
    ],
  );

  const canView = (module: string) => can(module, "view");
  const canCreate = (module: string) => can(module, "create");
  const canEdit = (module: string) => can(module, "edit");
  const canDelete = (module: string) => can(module, "delete");
  const canExport = (module: string) => can(module, "export");
  // Cost Analysis's `configure_alerts` or the Alerts module's `create`: the
  // server's alert rules accept either (`group_grant` on AlertRuleViewSet).
  const canConfigureAlerts = (module = "cost_analysis") =>
    can(module, "configure_alerts") || can("alerts", "create");

  /**
   * Admin-tier roles. Matches the backend ``roles_assignable_by`` hierarchy:
   * system → platform_manager → distributor → MSP → company admins.
   * MSP_DISTRIBUTOR was missing before (Phase U8), which caused admin-only
   * UI affordances to disappear for distributor admins even though the server
   * accepted their writes.
   */
  const isAdmin = useMemo(
    () =>
      user?.role === Role.PLATFORM_ADMIN ||
      user?.role === Role.PLATFORM_MANAGER ||
      user?.role === Role.DISTRIBUTOR_ADMIN ||
      user?.role === Role.MSP_ADMIN ||
      user?.role === Role.COMPANY_OWNER ||
      user?.role === Role.COMPANY_ADMIN,
    [user?.role],
  );

  /**
   * True for both full platform_admin AND platform_manager (matches backend
   * `is_platform_admin=True`). Use this for permission gates that the backend
   * grants to both roles.
   */
  const isSystemAdmin = useMemo(
    () =>
      user?.role === Role.PLATFORM_ADMIN ||
      user?.role === Role.PLATFORM_MANAGER,
    [user?.role],
  );

  /**
   * An unrestricted platform admin: the `platform_admin` role and no
   * permission group narrowing it -- never a platform_manager. Mirrors the
   * server's `is_full_platform_admin`, read from the token's claim of the
   * same name; a token minted before the claim existed falls back to the role.
   * Use it for platform-wide settings and anything that moves an organization
   * between branches.
   */
  const isFullSystemAdmin = useMemo(
    () =>
      user?.role === Role.PLATFORM_ADMIN &&
      (accessToken ? getJwtBoolClaim(accessToken, "is_full_platform_admin") : undefined) !== false,
    [user?.role, accessToken],
  );

  /**
   * May open impersonation at all: the server's `CanImpersonate`, read from
   * the token's `can_impersonate` claim (the `platform_admin` role, restricted
   * or not); a token without the claim falls back to the role. Which targets a
   * restricted admin may reach is the server's answer per target.
   */
  const canImpersonate = useMemo(() => {
    const claim = accessToken
      ? getJwtBoolClaim(accessToken, "can_impersonate")
      : undefined;
    return claim ?? user?.role === Role.PLATFORM_ADMIN;
  }, [user?.role, accessToken]);

  /** True when the user is an platform_manager (scoped platform_admin). */
  const isAccountManager = useMemo(
    () => user?.role === Role.PLATFORM_MANAGER,
    [user?.role],
  );

  const isMSPAdmin = useMemo(
    () => user?.role === Role.MSP_ADMIN,
    [user?.role],
  );
  const isDistributor = useMemo(
    () => user?.role === Role.DISTRIBUTOR_ADMIN,
    [user?.role],
  );

  /** Distributor UUIDs an platform_manager is allowed to access (from JWT). */
  const allowedDistributorIds = useMemo(
    () => user?.allowedDistributorIds ?? [],
    [user?.allowedDistributorIds],
  );

  /** MSP UUIDs an platform_manager is allowed to access (from JWT). */
  const allowedMspIds = useMemo(
    () => user?.allowedMspIds ?? [],
    [user?.allowedMspIds],
  );

  return {
    can,
    canView,
    canCreate,
    canEdit,
    canDelete,
    canExport,
    canConfigureAlerts,
    isAdmin,
    isSystemAdmin,
    isFullSystemAdmin,
    canImpersonate,
    isAccountManager,
    isMSPAdmin,
    isDistributor,
    allowedDistributorIds,
    allowedMspIds,
    /**
     * The organizations restricted platform staff are assigned, from the
     * token's `allowed_organization_ids`; `undefined` when unrestricted.
     */
    assignedOrganizationIds: platformAssignment,
    userGroups,
  };
}
